import AppNotificationProvider from '../../providers/appNotificationProvider'
import InfiniteScrollLoader from '~/infiniteScrollLoader'
import AppNotification from '../../../formability/notifications/models/appNotification'
import {AppNotificationType} from "../../../formability/notifications/enums/appNotificationType";
import Vue from "vue";
import {AppNotificationTab} from "../../../formability/notifications/enums/appNotificationTab";

export default function () {
  const appNotificationProvider = new AppNotificationProvider();

  const resetNotificationsAndMessageThreads = state => {
    state.notifications = [];
    state.messageThreads = {};
  }

  const sortMessageThreads = state => {
    for (const formSubmissionId in state.messageThreads) {
      const messageThread = state.messageThreads[formSubmissionId]
      state.messageThreads[formSubmissionId] = messageThread.sort((a, b) => {
        const date1 = new Date(a.c_at);
        const date2 = new Date(b.c_at);
        //@ts-ignore
        return date1 - date2;
      })
    }
  }

  const loadNewNotificationsIntoState = (state, notifications) => {
    for (const notification of notifications) {
      if (notification.isMessage) {
        state.loadedMessageIds.push(notification.id);
        const formSubmissionId = notification.document_id;
        const alreadyHaveMostRecentMessage = !!state.messageThreads[formSubmissionId];
        if (alreadyHaveMostRecentMessage) {
          const messageThread = state.messageThreads[formSubmissionId];
          if (messageThread.length < 5) {
            messageThread.push(notification);
          }
        } else {
          state.notifications.push(notification);
          state.messageThreads[formSubmissionId] = [];
        }
      } else {
        state.notifications.push(notification);
      }
    }
    sortMessageThreads(state);
  }

  return {
    namespaced: true,
    state: {
      appNotificationProvider,
      activeTab: AppNotificationTab.Everything,
      unreadNotifications: [],
      unreadCount: 0,
      unreadCountByTab: {},
      notifications: [],
      readNotification: {},
      recentlyUpdatedNotification: [],
      notificationsFullyLoaded: false,
      notificationsInfiniteLoader: new InfiniteScrollLoader(async function (requestData) {
        const result = await appNotificationProvider.fetch(requestData)
        return { result, objects: result.notifications }
      }),
      messageThreads: {}, // form_submission_id => [AppNotification, AppNotification, AppNotification...],
      loadedMessageIds: [],
    },
    mutations: {
      updateUnreadNotifications (state, {notifications, unread_count, unread_count_by_tab}) {
        state.unreadNotifications = notifications
        state.unreadCount = unread_count
        if (unread_count_by_tab) state.unreadCountByTab = unread_count_by_tab;
      },
      updateRecentlyReadNotifications (state, notifications) {
        state.recentlyUpdatedNotification = notifications
      },
      updateSavedNotifications(state, notification) {
        // we only need to update if we are on the Saved tab and we unsave the notification
        if (state.activeTab !== AppNotificationTab.Saved || notification.is_saved) return;
        if (state.messageThreads[notification.document_id]) {
          // if the notification is a message... Then we want to clear the message thread as well
          Vue.set(state.messageThreads, notification.document_id, []);
        }
        const notificationToRemove = state.notifications.find(notif => notif.id === notification.id);
        const index = state.notifications.indexOf(notificationToRemove);
        state.notifications.splice(index, 1);
      },
      updateNotifications (state, { objects, isNewQuery }) {
        const notificationObjects = objects.map((d) => {
          let notification = AppNotification.repo.find(d.id)
          if (notification) return notification

          notification = new AppNotification()
          notification = notification.loadRootFromJson(d)
          return notification
        })
        if (isNewQuery) {
          resetNotificationsAndMessageThreads(state);
        }
        loadNewNotificationsIntoState(state, notificationObjects);
      },
      setNotificationsFullyLoaded (state, newValue) {
        state.notificationsFullyLoaded = newValue
      },
      updateMostRecentMessage(state, newestReply) {
        const formSubmissionId = newestReply.document_id;
        const previousMessage = state.notifications.find(notification => {
          return notification.document_id === formSubmissionId;
        })
        const previousMessageIndex = state.notifications.indexOf(previousMessage);
        state.notifications.splice(previousMessageIndex, 1, newestReply);
        let messageThread = state.messageThreads[formSubmissionId];
        state.messageThreads[formSubmissionId].push(previousMessage);
        if (messageThread.length > 5) messageThread = messageThread.slice(1);
        Vue.set(state.messageThreads, formSubmissionId, messageThread);
      },
      setTab(state, newTab) {
        state.activeTab = newTab;
      }
    },
    actions: {
      async loadNotifications ({ commit, state }, query) {
        const messageNotificationIds = state.loadedMessageIds;
        query['message_notification_ids'] = messageNotificationIds;
        const r = await state.notificationsInfiniteLoader.fetch(query);
        commit('updateNotifications', { objects: r.objects, isNewQuery: r.isNewQuery })
        commit('setNotificationsFullyLoaded', r.isFullyLoaded)
        return r.result
      },
      async markNotificationsRead ({ commit, state }, notifications) {
        const result = await state.appNotificationProvider.markAllAsRead(notifications)
        commit('updateUnreadNotifications', {notifications: result.notifications, unread_count: result.unread_count, unread_count_by_tab: result.unread_count_by_tab})
        return result
      },
      async toggleSave({ commit, state }, notification) {
        let result = await state.appNotificationProvider.toggleSave(notification);
        commit('updateSavedNotifications', result.notification);
        commit('updateUnreadNotifications', {notifications: result.notifications, unread_count: result.unread_count, unread_count_by_tab: result.unread_count_by_tab})
        return result;
      },
      checkForUnreadNotifications ({ commit, state }) {
        return new Promise((resolve, reject) => {
          state.appNotificationProvider.fetchUnread().then(result => {
            commit('updateUnreadNotifications', result)
            resolve()
          })
        })
      },
      clearNotifications ({ commit, state, getters }) {
        return new Promise((resolve, reject) => {
          state.appNotificationProvider.markAllAsRead(state.unreadNotifications).then(result => {
            commit('updateUnreadNotifications', {notifications: result.notifications, unread_count: result.unread_count})
            resolve()
          })
        })
      },
      async markAsRead ({ commit, state }, notification) {
        let result = await state.appNotificationProvider.markNotificationAsRead(notification)
        commit('updateUnreadNotifications', {notifications: result.notifications, unread_count: result.unread_count, unread_count_by_tab: result.unread_count_by_tab})
        return result
      },
      async updateNotification ({ commit, state }, notification) {
        let result = null
        if (notification.is_read) {
          result = await state.appNotificationProvider.markNotificationAsUnread(notification)
        } else {
          result = await state.appNotificationProvider.markNotificationAsRead(notification)
        }
        commit('updateUnreadNotifications', {notifications: result.notifications, unread_count: result.unread_count, unread_count_by_tab: result.unread_count_by_tab})
        return result
      },
      sendResponseMessage({ commit, state }, { json }) {
        state.appNotificationProvider.sendResponseMessage(json).then(result => {
          commit('updateMostRecentMessage', result.notification);
          return result;
        })
      }
    }
  }
}